/**
 * firestoreCache.js — a shared translation cache backed by Firestore.
 *
 * Factory: `createFirestoreCache(collectionName, norm)` → a cache object the i18n
 * engine can read from and write into. Page-agnostic — each page passes its own
 * collection (e.g. 'opsTranslations'). Multi-language doc schema, keyed by the
 * NORMALIZED source label so each item is stored once and shared across all devices:
 *
 *   {collection}/{normSource}
 *     { en: '<source label>', t: { te:'…', hi:'…' }, src: { te:'auto'|'human' }, at }
 *
 * Human corrections (`src[lang]='human'`) are authoritative and never overwritten by
 * auto-fill. This is the ONLY file here that imports Firebase, so the rest of the i18n
 * library stays dependency-light and easy to unit-test.
 *
 * ── Why this is a one-shot read, not a listener ──────────────────────────────
 * This used to hold an onSnapshot listener over the whole collection, re-reading up
 * to CACHE_LIMIT documents EVERY time a screen mounted. In dev that is every hot
 * reload, doubled again by StrictMode's double-mount, so a single afternoon of
 * editing the ticket board could drain the project's entire daily Firestore read
 * quota — and dev and prod share one project.
 *
 * Translations are near-static reference data: a term is written once and then read
 * forever. That does not need a live socket. Now one fetch per collection fills a
 * module-level session copy, every later mount is served from memory for zero reads,
 * and concurrent callers share a single in-flight request.
 *
 * TRADE-OFF: a correction made on another device is no longer pushed instantly — it
 * lands on the next load, or after CACHE_TTL_MS. The local device still sees its own
 * edits immediately, because the engine merges them optimistically (createI18n's
 * `mergeIn`) and `pokeSession` below keeps them in the session copy across remounts.
 *
 * Once the session copy has expired, the refresh asks only for entries written since
 * the newest one it holds (every write stamps `at`) — a handful of docs instead of the
 * whole collection each time a screen remounts after a quiet spell (the 🧺 drawer on
 * the ticket board remounts Daily Ops on every open).
 */
import { collection, doc, query, where, limit, getDocs, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/** Max docs pulled in one fetch. A ceiling, not an expectation. */
export const CACHE_LIMIT = 3000;

/** How long a session copy is trusted before the next load refetches. */
export const CACHE_TTL_MS = 10 * 60 * 1000;

/** collectionName → { map: Map|null, at: number, newest: Timestamp|null, inflight: Promise|null } */
const sessions = new Map();

function session(name) {
  let s = sessions.get(name);
  if (!s) { s = { map: null, at: 0, newest: null, inflight: null }; sessions.set(name, s); }
  return s;
}

const atMs = (v) => (v && v.at && typeof v.at.toMillis === 'function' ? v.at.toMillis() : -1);

/**
 * Merge one entry into the session copy, so a write made on this device survives a
 * remount instead of vanishing until the TTL lapses.
 */
function pokeSession(name, key, { en, lang, text, srcType }) {
  const s = sessions.get(name);
  if (!s || !s.map) return;
  const cur = s.map.get(key) || { en, t: {}, src: {} };
  s.map.set(key, {
    ...cur,
    en: en || cur.en,
    t:   { ...cur.t,   [lang]: text },
    src: { ...cur.src, [lang]: srcType },
  });
}

/** Drop cached collections — for tests, and for a forced refresh. */
export function resetTranslationCache(name) {
  if (name) sessions.delete(name); else sessions.clear();
}

export function createFirestoreCache(collectionName, norm) {
  return {
    /**
     * Read the cache once → Map(normSource → { en, t, src }).
     *
     * Served from the session copy when warm (zero reads). When cold, every
     * concurrent caller shares ONE in-flight fetch — which is what makes
     * StrictMode's double-mount cost a single read instead of two.
     *
     * Returns a cancel function, so an unmounted caller is not called back.
     */
    load(onData, onError) {
      let cancelled = false;
      const s = session(collectionName);

      if (s.map && Date.now() - s.at < CACHE_TTL_MS) {
        // Warm. Deliver asynchronously anyway, so callers see one consistent
        // "resolves later" shape rather than a sometimes-synchronous callback.
        Promise.resolve().then(() => { if (!cancelled) onData(new Map(s.map)); });
        return () => { cancelled = true; };
      }

      if (!s.inflight) {
        // An expired copy only needs what was written since its newest entry (`>=`:
        // entries stamped in the same instant are re-read rather than missed).
        const since = s.map && s.newest;
        const q = since
          ? query(collection(db, collectionName), where('at', '>=', since), limit(CACHE_LIMIT))
          : query(collection(db, collectionName), limit(CACHE_LIMIT));
        s.inflight = getDocs(q)
          .then((snap) => {
            const m = since ? new Map(s.map) : new Map();
            let newest = since || null;
            snap.docs.forEach((d) => {
              const v = d.data();
              if (!v || !v.en) return;
              m.set(d.id, v);
              if (atMs(v) > (newest ? newest.toMillis() : -1)) newest = v.at;
            });
            s.map = m;
            s.at = Date.now();
            s.newest = newest;
            return m;
          })
          .finally(() => { s.inflight = null; });   // a failure retries on next mount
      }
      s.inflight.then(
        (m) => { if (!cancelled) onData(new Map(m)); },
        (e) => { if (!cancelled && onError) onError(e); },
      );
      return () => { cancelled = true; };
    },

    /** Write an AUTO translation for one language — skips if a value/human entry exists. */
    async saveAuto(source, lang, text) {
      const key = norm(source);
      if (!key || !text) return;
      const ref = doc(db, collectionName, key);
      // Still a real read: this guard protects another device's human correction,
      // and the session copy may be up to CACHE_TTL_MS stale. It only runs for
      // genuinely new terms, which the engine attempts once per session.
      const existing = await getDoc(ref);
      const data = existing.exists() ? existing.data() : null;
      if (data && data.src && data.src[lang] === 'human') return;   // never clobber a correction
      if (data && data.t && data.t[lang]) return;                    // already translated
      const en = String(source).trim();
      await setDoc(ref, {
        en,
        t: { ...(data && data.t), [lang]: String(text).trim() },
        src: { ...(data && data.src), [lang]: 'auto' },
        at: serverTimestamp(),
      }, { merge: true });
      pokeSession(collectionName, key, { en, lang, text: String(text).trim(), srcType: 'auto' });
    },

    /** Write a HUMAN correction for one language — authoritative. */
    async saveHuman(source, lang, text) {
      const key = norm(source);
      if (!key || !text) return;
      const en = String(source).trim();
      await setDoc(doc(db, collectionName, key), {
        en,
        t: { [lang]: String(text).trim() },
        src: { [lang]: 'human' },
        at: serverTimestamp(),
      }, { merge: true });
      pokeSession(collectionName, key, { en, lang, text: String(text).trim(), srcType: 'human' });
    },
  };
}
